import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { QuestionIcon } from "@phosphor-icons/react/dist/csr/Question";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { INLINE_CONTROL } from "@/profitability/constants";
import { useReportCurrency } from "@/profitability/currency";
import type { RunGoalChecks } from "@/profitability/settings/types";
import { toleranceLabel } from "@/profitability/settings/values";
import { formatValue } from "./format";
import { runGoalHeadline, runGoalMatches } from "./run-goal";

/**
 * A period against the owner's own numbers, as a compact mark: "Matches your
 * numbers" or "3 figures off", which opens the figures with what the owner
 * expected beside what the report gives.
 */
export const RunGoalStatus = ({
  checks,
  inline = false,
}: {
  checks: RunGoalChecks;
  /** Inside a line of text: an underlined phrase rather than a button. */
  inline?: boolean;
}) => {
  const currency = useReportCurrency();
  const matches = runGoalMatches(checks);
  const checked = checks.status === "checked";
  const Icon = matches ? CheckCircleIcon : checked ? XCircleIcon : QuestionIcon;
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            size="sm"
            variant={inline ? "link" : "ghost"}
            color={inline ? "neutral" : undefined}
            className={cn({ [INLINE_CONTROL]: inline })}
            data-testid="run-goal-status"
          >
            <Icon
              className={cn({
                "size-4": !inline,
                "size-3.5": inline,
                "text-success": matches,
                "text-danger": checked && !matches,
                "text-subtle": !checked,
              })}
            />
            {runGoalHeadline(checks)}
          </Button>
        }
      />
      <PopoverContent align="start" className="w-96">
        <Stack gap={2} align="stretch">
          <Text variant="label">
            Checked against {checks.document.file_name}
          </Text>
          {checks.status === "checked" ? (
            <>
              {checks.failed.length > 0 && (
                <table className="w-full text-left">
                  <thead>
                    <tr>
                      <Text render={<th />} variant="caption" tone="muted">
                        Figure
                      </Text>
                      <Text render={<th />} variant="caption" tone="muted">
                        Yours
                      </Text>
                      <Text render={<th />} variant="caption" tone="muted">
                        Report
                      </Text>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.failed.map((check) => (
                      <tr key={check.label}>
                        <Text render={<td />} className="pr-2 align-top">
                          {check.label}
                          {check.error && (
                            <Text variant="caption" tone="muted">
                              {check.error}
                            </Text>
                          )}
                        </Text>
                        <Text render={<td />} tabular className="pr-2">
                          {formatValue(check.expected, check.type, currency)}
                        </Text>
                        <Text render={<td />} tabular>
                          {formatValue(check.actual, check.type, currency)}
                        </Text>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Text variant="caption" tone="muted">
                {checks.passed} of {checks.total} figures match,{" "}
                {toleranceLabel(checks.tolerance, currency)}.
              </Text>
            </>
          ) : (
            <Text tone="muted">{checks.reason}</Text>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
